<template>
  <div class="category-selector">书籍分类：
    <!-- 输入框显示已选中项目 -->
    <el-input
      v-model="selectedText"
      :placeholder="placeholder"
      readonly
      @focus="showTree = true"
      style="cursor: pointer; margin-bottom: 10px;"
    >
      <template #suffix>
        <i 
          :class="['el-icon-arrow-down', { 'rotate-180': showTree }]"
          style="transition: transform 0.3s;"
        ></i>
      </template>
    </el-input>

    <!-- 树形选择面板 -->
    <div v-if="showTree" class="tree-panel">
      <!-- 搜索框 -->
      <div style="margin-bottom: 10px; margin-left: 10px;display: flex; align-items: center; gap: 10px;">
        <el-button
            type="text"
            icon="el-icon-s-unfold"
            size="big"
            @click="expandAll"
        >展开</el-button>
        <el-button
            type="text"
            icon="el-icon-s-fold"
            size="big"
            @click="collapseAll"
        >折叠</el-button>
      </div>

      <!-- 树形组件 -->
      <el-tree
        ref="categoryTree"
        :data="filteredCategoryData"
        :props="treeProps"
        :default-checked-keys="selectedIds"
        :filter-node-method="filterNode"
        node-key="id"
        show-checkbox
        :default-expand-all="false"
        :expand-on-click-node="false"
        :check-strictly="true"
        @check="handleCheckChange"
      >
        <span slot-scope="{ node, data }">
          <span>{{ node.label }}</span>
          <span v-if="data.children && data.children.length > 0" style="color: #909399; font-size: 12px;">
            ({{ data.children.length }})
          </span>
        </span>
      </el-tree>
      
      <!-- 底部按钮 -->
      <div style="margin-top: 10px; text-align: right;">
        <el-button type="primary" size="small" @click="showTree = false">
          确定
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategorySelector',
  props: {
    // 外部传入的分类数据
    categoryData: {
      type: Array,
      default: () => []
    },
    // 占位符文本
    placeholder: {
      type: String,
      default: '请选择分类'
    },
    // 默认选中的ID
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 控制树形面板显示
      showTree: false,
      // 搜索文本
      searchText: '',
      // 是否只看一级分类
      showOnlyLevel1: false,
      treeProps: {
        children: 'children',
        label: 'label',
        disabled: this.checkNodeDisabled
      },
      // 存储选中节点信息 {id, label, parentId, parentLabel, isLeaf}
      selectedNodes: new Map(),
      // 展开状态
      expandedKeys: [],

      // 保存上次选中的数组值
      lastSelectValueArr: [],
      // 保存上次选中的节点
      lastSelectNode: {},
      isTwoEqualArr: false,
    };
  },
  computed: {
    // 输入框中显示的文本
    selectedText() {
      if (this.selectedNodes.size === 0) {
        return '';
      }
      
      const displayTexts = [];

      let ispush = true;   //控制一级分类不要重复显示在el-input中
      this.selectedNodes.forEach(node => {
        if (node.isLeaf && node.parentLabel) {
          // 二级分类：一级分类-二级分类
          displayTexts.push(`${node.parentLabel}-${node.label}`);
          ispush = false;   //控制一级分类不要重复显示在el-input中
        } else {
          if(ispush){    //控制一级分类不要重复显示在el-input中
              displayTexts.push(node.label);
          }
          ispush = true;   //控制一级分类不要重复显示在el-input中
        }
      });
      
      return displayTexts.join(', ');
    },
    
    // 获取选中的ID数组
    selectedIds() {
      return Array.from(this.selectedNodes.keys());
    },
    
    // 获取过滤后的分类数据
    filteredCategoryData() {
      if (this.showOnlyLevel1) {
        // 只显示一级分类
        return this.categoryData.map(category => ({
          ...category,
          children: []
        }));
      }
      
      if (!this.searchText) {
        return this.categoryData;
      }
      
      // 搜索过滤
      return this.filterTreeData(this.categoryData, this.searchText);
    }
  },
  watch: {
    // 监听外部传入的默认值
    value: {
      immediate: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length > 0) {
          this.initSelectedNodes(newVal);   //接收主vue传送的初始化el-input显示值和tree选中的记录值
          console.log("设置初始化的值====" + newVal)
        } else {
          this.selectedNodes.clear();
          this.updateTreeSelection();
        }
      }
    },
    
    // 监听选中节点变化，向父组件传递值
    selectedIds: {
      handler(newVal) {
        this.$emit('input', newVal);
        this.$emit('change', {
          ids: newVal,
          nodes: this.getSelectedNodes()
        });
      },
      deep: true
    },
    
    // 监听数据变化
    categoryData: {
      immediate: true,
      deep: true,
      handler() {
        this.updateTreeSelection();
        this.expandAll();
      }
    }
  },
  mounted() {
    // 添加全局点击事件监听
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    // 过滤树数据
    filterTreeData(data, keyword) {
      if (!keyword) return data;
      
      const filter = (nodes) => {
        return nodes.filter(node => {
          // 如果节点标签包含关键词
          if (node.label && node.label.toLowerCase().includes(keyword.toLowerCase())) {
            return true;
          }
          
          // 如果有子节点，递归过滤
          if (node.children && node.children.length > 0) {
            const filteredChildren = filter(node.children);
            if (filteredChildren.length > 0) {
              return true;
            }
          }
          
          return false;
        });
      };
      
      return filter(JSON.parse(JSON.stringify(this.categoryData)));
    },
    
    // 搜索处理
    handleSearch() {
      this.$refs.categoryTree.filter(this.searchText);
    },
    
    // 过滤节点
    filterNode(value, data) {
      if (!value) return true;
      return data.label.toLowerCase().includes(value.toLowerCase());
    },
    
    // 展开所有节点
    expandAll() {
      if (!this.$refs.categoryTree) return;
      
      this.expandedKeys = [];
      const traverse = (nodes) => {
        nodes.forEach(node => {
          this.expandedKeys.push(node.id);
          if (node.children && node.children.length > 0) {
            traverse(node.children);
          }
        });
      };
      
      traverse(this.filteredCategoryData);
      
      this.expandedKeys.forEach(key => {
        const node = this.$refs.categoryTree.getNode(key);
        if (node) {
          node.expanded = true;
        }
      });
    },
    
    // 折叠所有节点
    collapseAll() {
      if (!this.$refs.categoryTree) return;
      
      this.expandedKeys.forEach(key => {
        const node = this.$refs.categoryTree.getNode(key);
        if (node) {
          node.expanded = false;
        }
      });
      this.expandedKeys = [];
    },
    
    // 切换只看一级分类
    toggleLevel1Filter() {
      this.$nextTick(() => {
        if (this.showOnlyLevel1) {
          this.expandAll();
        }
      });
    },
    
    // 初始化选中节点
    initSelectedNodes(ids) {
      this.selectedNodes.clear();
      
      ids.forEach(id => {
        const nodeInfo = this.findNodeById(id);
        if (nodeInfo) {
          const { node, parent } = nodeInfo;
          this.selectedNodes.set(node.id, {
            id: node.id,
            label: node.label,
            parentId: parent ? parent.id : null,
            parentLabel: parent ? parent.label : null,
            isLeaf: !node.children || node.children.length === 0
          });
        }
      });
      
      this.updateTreeSelection();
    },
    
    // 根据ID查找节点
    findNodeById(id) {
      for (const category of this.categoryData) {
        if (category.id === id) {
          return { node: category, parent: null };
        }
        if (category.children && category.children.length > 0) {
          const child = category.children.find(item => item.id === id);
          if (child) {
            return { node: child, parent: category };
          }
        }
      }
      return null;
    },
    
    // 检查节点是否应禁用
    checkNodeDisabled(data, node) {
      // 如果是一级分类且有子节点，则禁用
      if (data.children && data.children.length > 0) {
        return true;
      }
      return false;
    },
    
    handleCheckChange(data, checkedInfo) {
      const checkedKeys = checkedInfo.checkedKeys || [];
      const checkedNodes = checkedInfo.checkedNodes || [];
      // 处理选中逻辑
      this.updateSelectedNodes(checkedKeys, checkedNodes);
    },
    
    // 查找父节点
    findParent(childNode) {
      for (const category of this.categoryData) {
        if (category.id === childNode.id) {
          return null; // 当前就是一级节点
        }
        if (category.children && category.children.length > 0) {
          const child = category.children.find(item => item.id === childNode.id);
          if (child) {
            return category;
          }
        }
      }
      return null;
    },
    
    // 更新选中节点
    updateSelectedNodes(checkedKeys, checkedNodes) {
      const newSelectedNodes = new Map();
      
      // 按一级分类分组
      const categoryMap = new Map();
      
      checkedNodes.forEach(node => {
        const parent = this.findParent(node);
        const categoryId = parent ? parent.id : node.id;
        
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, []);
        }
        categoryMap.get(categoryId).push(node);
      });
      
      // 处理每个分类的选择
      categoryMap.forEach((nodes, categoryId) => {
        const category = this.categoryData.find(cat => cat.id == categoryId);
        if (nodes.length === 0) return;


        if (category && category.children && category.children.length > 0) {
          // 有子节点的分类
          if (nodes.length === 1 && !nodes[0].children) {
            // 只选中了一个二级分类
            const node = nodes[0];
            newSelectedNodes.set(node.id, {
              id: node.id,
              label: node.label,
              parentId: category.id,
              parentLabel: category.label,
              isLeaf: true
            });
            // 同时选中一级分类
            newSelectedNodes.set(category.id, {
              id: category.id,
              label: category.label,
              parentId: null,
              parentLabel: null,
              isLeaf: false
            });
          } else {
            // 选中了多个子节点，只保留最后一个
            let lastNode = nodes[nodes.length - 1];
            this.lastSelectNode = lastNode;
            if (!lastNode.children) {
              // 最后一个节点是二级分类
              newSelectedNodes.set(lastNode.id, {
                id: lastNode.id,
                label: lastNode.label,
                parentId: category.id,
                parentLabel: category.label,
                isLeaf: true
              });

              // 同时选中一级分类
              newSelectedNodes.set(category.id, {
                id: category.id,
                label: category.label,
                parentId: null,
                parentLabel: null,
                isLeaf: false
              });
            } else {
              // 最后一个节点是一级分类（不允许）
              // 不添加到选中列表
            }
          }
        } else {
          // 无子节点的分类
          if (nodes.length === 1) {
            const node = nodes[0];
            newSelectedNodes.set(node.id, {
              id: node.id,
              label: node.label,
              parentId: null,
              parentLabel: null,
              isLeaf: false
            });
          } else {
            // 选中了多个一级分类，只保留最后一个
            const lastNode = nodes[nodes.length - 1];
            newSelectedNodes.set(lastNode.id, {
              id: lastNode.id,
              label: lastNode.label,
              parentId: null,
              parentLabel: null,
              isLeaf: false
            });
          }
        }
      });
      
      // 更新树的选择状态
      this.selectedNodes = newSelectedNodes;
      this.updateTreeSelection();
    },
    
    // 更新树的选择状态
    updateTreeSelection() {
      this.$nextTick(() => {
        if (this.$refs.categoryTree) {
          this.$refs.categoryTree.setCheckedKeys(this.selectedIds);
        }
      });
    },
    
    // 获取选中的节点信息
    getSelectedNodes() {
      return Array.from(this.selectedNodes.values());
    },
    
    // 获取选中的ID数组
    getSelectedIds() {
      return this.selectedIds;
    },
    
    // 清空选择
    clearSelection() {
      this.selectedNodes.clear();
      this.updateTreeSelection();
    },
    
    // 点击页面其他区域关闭树形面板
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showTree = false;
      }
    }
  }
};
</script>

<style scoped>
.category-selector {
  position: relative;
  width: 100%;
}

.tree-panel {
  position: absolute;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 2000;
  padding: 10px;
  width: calc(100% - 2px);
  max-height: 400px;
  overflow-y: auto;
  margin-top: 5px;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>


// WEBPACK FOOTER //
// src/views/ztestapp/CategorySelector.vue